import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Search, Edit2, AlertTriangle, X } from 'lucide-react';
import Navbar from '@/components/Navbar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AVAILABLE_ALLERGENS = [
  'Dairy', 'Eggs', 'Fish', 'Gluten', 'Nuts', 'Shellfish', 'Soy', 'Sesame'
];

export default function FoodBankManager({ user, onLogout }) {
  const navigate = useNavigate();
  const [foodItems, setFoodItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);
  const [editFormData, setEditFormData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showAllergenDialog, setShowAllergenDialog] = useState(false);
  const [selectedAllergens, setSelectedAllergens] = useState([]);

  useEffect(() => {
    fetchFoodBank();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = foodItems.filter(item =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.category.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredItems(filtered);
    } else {
      setFilteredItems(foodItems);
    }
  }, [searchTerm, foodItems]);

  const fetchFoodBank = async () => {
    try {
      const response = await axios.get(`${API}/food-bank?limit=1000`);
      // The endpoint returns {foods: [], count: X}
      const items = response.data.foods || [];
      setFoodItems(items);
      setFilteredItems(items);
    } catch (error) {
      console.error('Food bank fetch error:', error);
      toast.error('Failed to fetch food bank');
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    setEditFormData({
      name: item.name,
      category: item.category,
      primary_unit: item.primary_unit,
      primary_unit_grams: item.primary_unit_grams,
      per_100g: { ...item.per_100g },
      allergens: item.allergens || []
    });
  };

  const openAllergenDialog = (item) => {
    setEditingItem(item);
    setSelectedAllergens(item.allergens || []);
    setShowAllergenDialog(true);
  };

  const toggleAllergen = (allergen) => {
    if (selectedAllergens.includes(allergen)) {
      setSelectedAllergens(selectedAllergens.filter(a => a !== allergen));
    } else {
      setSelectedAllergens([...selectedAllergens, allergen]);
    }
  };

  const saveAllergens = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/food-bank/${editingItem.id}/allergens`, {
        allergens: selectedAllergens
      });
      
      // Update local state
      setFoodItems(foodItems.map(item =>
        item.id === editingItem.id ? { ...item, allergens: selectedAllergens } : item
      ));
      
      toast.success('Allergens updated!');
      setShowAllergenDialog(false);
      setEditingItem(null);
    } catch (error) {
      toast.error('Failed to update allergens');
    } finally {
      setSaving(false);
    }
  };

  const saveFoodItem = async () => {
    setSaving(true);
    try {
      // Prepare data for backend
      const updateData = {
        name: editFormData.name,
        category: editFormData.category,
        primary_unit: editFormData.primary_unit,
        primary_unit_grams: parseFloat(editFormData.primary_unit_grams),
        countable: editingItem.countable || false,
        increment_type: editingItem.increment_type || 'decimal',
        kcal_per_100g: parseFloat(editFormData.per_100g.kcal),
        protein_per_100g: parseFloat(editFormData.per_100g.protein_g),
        carbs_per_100g: parseFloat(editFormData.per_100g.carbs_g),
        fat_per_100g: parseFloat(editFormData.per_100g.fat_g)
      };

      await axios.put(`${API}/food-bank/${editingItem.id}`, updateData);
      
      // Update local state
      setFoodItems(foodItems.map(item =>
        item.id === editingItem.id ? {
          ...item,
          name: editFormData.name,
          category: editFormData.category,
          primary_unit: editFormData.primary_unit,
          primary_unit_grams: editFormData.primary_unit_grams,
          per_100g: editFormData.per_100g
        } : item
      ));
      
      toast.success('Food item updated!');
      setEditingItem(null);
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.response?.data?.detail || 'Failed to update food item');
    } finally {
      setSaving(false);
    }
  };

  const autoFixAllergens = async () => {
    try {
      const response = await axios.post(`${API}/food-bank/auto-correct-allergens`);
      toast.success(`Fixed ${response.data.corrections_made} allergen tags!`);
      fetchFoodBank(); // Refresh
    } catch (error) {
      toast.error('Failed to auto-fix allergens');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-64">
          <p>Loading Food Bank...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar user={user} onLogout={onLogout} />
      
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Food Bank Manager</h1>
          <p className="text-gray-600">Manage allergens for food items</p>
        </div>

        {/* Actions Bar */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    placeholder="Search food items..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              {/* Auto-Fix Button */}
              <Button
                onClick={autoFixAllergens}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <AlertTriangle className="w-4 h-4 mr-2" />
                Auto-Fix All Allergens
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Food Items Grid */}
        <div className="grid gap-4">
          {filteredItems && filteredItems.length > 0 ? filteredItems.map((item) => (
            <Card key={item.id} className="hover:shadow-md transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between gap-4">
                  {/* Item Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-semibold text-lg">{item.name}</h3>
                      <Badge variant="outline" className="text-xs">{item.category}</Badge>
                    </div>
                    
                    {/* Macros per 100g */}
                    <div className="grid grid-cols-4 gap-3 mb-3 text-sm">
                      <div>
                        <p className="text-gray-500 text-xs">Calories</p>
                        <p className="font-semibold">{item.per_100g?.kcal || 0} kcal</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs">Protein</p>
                        <p className="font-semibold">{item.per_100g?.protein_g || 0}g</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs">Carbs</p>
                        <p className="font-semibold">{item.per_100g?.carbs_g || 0}g</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs">Fat</p>
                        <p className="font-semibold">{item.per_100g?.fat_g || 0}g</p>
                      </div>
                    </div>

                    {/* Unit Info */}
                    <div className="flex gap-4 mb-3 text-sm">
                      <div>
                        <span className="text-gray-500">Unit:</span> <span className="font-medium">{item.primary_unit}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Per unit:</span> <span className="font-medium">{item.primary_unit_grams}g</span>
                      </div>
                    </div>
                    
                    {/* Allergens */}
                    <div className="flex flex-wrap gap-2">
                      <span className="text-xs text-gray-500">Allergens:</span>
                      {item.allergens && item.allergens.length > 0 ? (
                        item.allergens.map((allergen, idx) => (
                          <Badge key={idx} variant="destructive" className="text-xs">
                            {allergen}
                          </Badge>
                        ))
                      ) : (
                        <Badge variant="outline" className="text-xs">None</Badge>
                      )}
                    </div>

                    {/* Check for "free" in name */}
                    {item.name.toLowerCase().includes('free') && item.allergens && item.allergens.length > 0 && (
                      <div className="mt-2">
                        <Badge variant="outline" className="text-xs bg-yellow-50 border-yellow-300 text-yellow-800">
                          ⚠️ Has "free" in name but allergens present
                        </Badge>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEditDialog(item)}
                    >
                      <Edit2 className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openAllergenDialog(item)}
                    >
                      <AlertTriangle className="w-4 h-4 mr-1" />
                      Allergens
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )) : (
            <Card>
              <CardContent className="pt-6 text-center text-gray-500">
                {searchTerm ? 'No items found matching your search' : 'No food items in database'}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Results Count */}
        <div className="mt-4 text-center text-sm text-gray-500">
          Showing {filteredItems.length} of {foodItems.length} items
        </div>
      </div>

      {/* Edit Food Item Dialog */}
      <Dialog open={!!editingItem && !showAllergenDialog} onOpenChange={() => setEditingItem(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Food Item</DialogTitle>
            <DialogDescription>
              Update food item details (allergen editing in separate dialog)
            </DialogDescription>
          </DialogHeader>

          {editFormData && (
            <div className="space-y-4">
              {/* Name */}
              <div>
                <Label>Name</Label>
                <Input
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({...editFormData, name: e.target.value})}
                />
              </div>

              {/* Category */}
              <div>
                <Label>Category</Label>
                <Input
                  value={editFormData.category}
                  onChange={(e) => setEditFormData({...editFormData, category: e.target.value})}
                />
              </div>

              {/* Unit Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Primary Unit</Label>
                  <Input
                    value={editFormData.primary_unit}
                    onChange={(e) => setEditFormData({...editFormData, primary_unit: e.target.value})}
                    placeholder="g, tbsp, cup, piece"
                  />
                </div>
                <div>
                  <Label>Grams per Unit</Label>
                  <Input
                    type="number"
                    value={editFormData.primary_unit_grams}
                    onChange={(e) => setEditFormData({...editFormData, primary_unit_grams: parseFloat(e.target.value)})}
                  />
                </div>
              </div>

              {/* Macros per 100g */}
              <div>
                <Label className="font-semibold mb-2 block">Macros per 100g</Label>
                <div className="grid grid-cols-4 gap-3">
                  <div>
                    <Label className="text-xs">Calories (kcal)</Label>
                    <Input
                      type="number"
                      value={editFormData.per_100g.kcal}
                      onChange={(e) => setEditFormData({
                        ...editFormData,
                        per_100g: {...editFormData.per_100g, kcal: parseFloat(e.target.value)}
                      })}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Protein (g)</Label>
                    <Input
                      type="number"
                      value={editFormData.per_100g.protein_g}
                      onChange={(e) => setEditFormData({
                        ...editFormData,
                        per_100g: {...editFormData.per_100g, protein_g: parseFloat(e.target.value)}
                      })}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Carbs (g)</Label>
                    <Input
                      type="number"
                      value={editFormData.per_100g.carbs_g}
                      onChange={(e) => setEditFormData({
                        ...editFormData,
                        per_100g: {...editFormData.per_100g, carbs_g: parseFloat(e.target.value)}
                      })}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Fat (g)</Label>
                    <Input
                      type="number"
                      value={editFormData.per_100g.fat_g}
                      onChange={(e) => setEditFormData({
                        ...editFormData,
                        per_100g: {...editFormData.per_100g, fat_g: parseFloat(e.target.value)}
                      })}
                    />
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-between pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowAllergenDialog(true);
                    setSelectedAllergens(editFormData.allergens);
                  }}
                >
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  Edit Allergens
                </Button>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setEditingItem(null)}>
                    Cancel
                  </Button>
                  <Button onClick={saveFoodItem} disabled={saving}>
                    {saving ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Allergens Dialog (Separate) */}
      <Dialog open={showAllergenDialog} onOpenChange={setShowAllergenDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Allergens</DialogTitle>
            <DialogDescription>
              {editingItem?.name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Allergen Checkboxes */}
            <div className="space-y-2">
              <Label className="font-semibold">Select Allergens:</Label>
              {AVAILABLE_ALLERGENS.map((allergen) => (
                <div key={allergen} className="flex items-center space-x-2">
                  <Checkbox
                    id={allergen}
                    checked={selectedAllergens.includes(allergen)}
                    onCheckedChange={() => toggleAllergen(allergen)}
                  />
                  <label
                    htmlFor={allergen}
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    {allergen}
                  </label>
                </div>
              ))}
            </div>

            {/* Selected Allergens Preview */}
            <div>
              <Label className="font-semibold mb-2 block">Selected:</Label>
              <div className="flex flex-wrap gap-2">
                {selectedAllergens.length > 0 ? (
                  selectedAllergens.map((allergen) => (
                    <Badge key={allergen} variant="destructive">
                      {allergen}
                      <X
                        className="w-3 h-3 ml-1 cursor-pointer"
                        onClick={() => toggleAllergen(allergen)}
                      />
                    </Badge>
                  ))
                ) : (
                  <span className="text-sm text-gray-500">No allergens selected</span>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowAllergenDialog(false)}>
                Cancel
              </Button>
              <Button onClick={saveAllergens} disabled={saving}>
                {saving ? 'Saving...' : 'Save Allergens'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
